/**
 * Declarative exposure and click tracking.
 *
 * Exposure:
 *   Add `.module-exposure-view`, `data-event`, and
 *   `data-event-parameters` to the module element.
 *   `data-exposure-repeat` defaults to `true`: the same element can report
 *   again after 30 seconds. Set it to `false` for once per page exposure.
 *
 * List scroll exposure:
 *   Add `.module-exposure-scroll`, `data-event`, and
 *   `data-event-parameters` to the list container, and `.module-scroll-item`
 *   plus `data-scroll-item-name` to every item. Plain text values are joined
 *   by `-` into `third_category`. JSON values containing `ecommerce.items`
 *   are merged into one ecommerce exposure payload for the visible batch.
 *
 * Click:
 *   Add `.custom-event-click`, `data-event`, and
 *   `data-event-parameters` to the clickable element.
 */
(function (window, document) {
  'use strict';

  // Theme sections/snippets can be rendered through more than one entry point.
  // Keep the delegated document listener and exposure observer singleton-safe.
  if (window.__brandCustomEventInitialized) return;
  window.__brandCustomEventInitialized = true;

  var EXPOSURE_SELECTOR = '.module-exposure-view';
  var SCROLL_EXPOSURE_SELECTOR = '.module-exposure-scroll';
  var SCROLL_ITEM_SELECTOR = '.module-scroll-item';
  var CLICK_SELECTOR = '.custom-event-click, [data-event][data-event-parameters]:not(.module-exposure-view):not(.module-exposure-scroll)';
  var EXPOSURE_THRESHOLD = 0.3;
  var EXPOSURE_REPEAT_INTERVAL = 30000;
  var SCROLL_EXPOSURE_FLUSH_DELAY = 50;
  var observedExposureElements = new WeakSet();
  var trackedExposureElements = new WeakSet();
  var lastExposureTimes = new WeakMap();
  var intersectingExposureElements = new Set();
  var exposureObserver = null;
  var scrollExposureObserver = null;
  var mutationObserver = null;
  var exposureCheckScheduled = false;
  var scrollExposureFlushScheduled = false;
  var scrollExposureFlushTimer = null;
  var observedScrollExposureItems = new WeakSet();
  var trackedScrollExposureItems = new WeakSet();
  var deferredScrollExposureItems = new Set();
  var scrollExposureItems = new Set();
  var pendingScrollExposureItems = new Map();

  function isElementRendered(element) {
    if (!element?.isConnected || !element.getClientRects().length) return false;

    var currentElement = element;
    while (currentElement) {
      var style = window.getComputedStyle(currentElement);
      if (style.display === 'none'
        || style.visibility === 'hidden'
        || style.visibility === 'collapse'
        || Number(style.opacity) === 0) {
        return false;
      }
      currentElement = currentElement.parentElement;
    }

    return true;
  }

  function isElementInViewport(element) {
    var rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    var viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    var visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
    var visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));

    return (visibleWidth * visibleHeight) / (rect.width * rect.height) >= EXPOSURE_THRESHOLD;
  }

  function isExposureVisible(element) {
    return isElementRendered(element) && isElementInViewport(element);
  }

  function isElementVisibleInContainer(element, container) {
    if (!isElementRendered(element) || !isElementRendered(container)) return false;

    var rect = element.getBoundingClientRect();
    var containerRect = container.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    var viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    var visibleLeft = Math.max(rect.left, containerRect.left, 0);
    var visibleRight = Math.min(rect.right, containerRect.right, viewportWidth);
    var visibleTop = Math.max(rect.top, containerRect.top, 0);
    var visibleBottom = Math.min(rect.bottom, containerRect.bottom, viewportHeight);
    var visibleWidth = Math.max(0, visibleRight - visibleLeft);
    var visibleHeight = Math.max(0, visibleBottom - visibleTop);

    return (visibleWidth * visibleHeight) / (rect.width * rect.height) >= EXPOSURE_THRESHOLD;
  }

  function parseEventParameters(element) {
    var parameters = element?.dataset?.eventParameters;
    if (!parameters) return {};

    try {
      return JSON.parse(parameters);
    } catch (error) {
      console.error('[CustomEvent] Invalid data-event-parameters JSON.', element, error);
      return null;
    }
  }

  function getAbsoluteUrl(url) {
    if (!url) return url;

    try {
      return new URL(url, window.location.href).href;
    } catch (error) {
      console.warn('[CustomEvent] Invalid target_url.', url, error);
      return url;
    }
  }

  function getTrackingParams(element) {
    var params = parseEventParameters(element);
    if (!params) return null;

    var belongAreaElement = element.closest('[data-belong-area]')
      || element.querySelector('[data-belong-area]');
    var customEventElement = element.closest('custom-event');

    params.event = element.dataset.event || params.event;
    params.page_type = element.dataset.pageType || params.page_type;
    params.event_label = element.dataset.eventLabel || params.event_label;
    params.event_action = element.dataset.eventAction || params.event_action;
    params.page_name = element.dataset.pageName
      || customEventElement?.dataset.pageName
      || window.gaGlobalVars?.page_name
      || params.page_name;
    params.belong_area = element.dataset.belongArea
      || belongAreaElement?.dataset.belongArea
      || params.belong_area;
    params.target_url = getAbsoluteUrl(element.dataset.targetUrl || params.target_url);

    return params;
  }

  function sendTrackingParams(params) {
    if (!params?.event) return null;
    if (window.brandTracking && typeof window.brandTracking.track === 'function') {
      return window.brandTracking.track(params);
    }

    console.warn('[CustomEvent] Tracking is unavailable.');
    return null;
  }

  function sendTrackingEvent(element) {
    return sendTrackingParams(getTrackingParams(element));
  }

  function trackExposureElement(element) {
    if (trackedExposureElements.has(element) || !isExposureVisible(element)) return null;

    var repeat = element.dataset.exposureRepeat !== 'false';
    var now = Date.now();
    var lastExposureTime = lastExposureTimes.get(element);
    if (repeat && lastExposureTime !== undefined
      && now - lastExposureTime < EXPOSURE_REPEAT_INTERVAL) return null;

    var payload = sendTrackingEvent(element);
    if (payload) {
      lastExposureTimes.set(element, now);
      if (!repeat) {
        trackedExposureElements.add(element);
        intersectingExposureElements.delete(element);
        exposureObserver?.unobserve(element);
      }
    }
    return payload;
  }

  function trackClickElement(element) {
    return sendTrackingEvent(element);
  }

  function createExposureObserver() {
    if (!('IntersectionObserver' in window)) return null;

    return new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || entry.intersectionRatio < EXPOSURE_THRESHOLD) {
          intersectingExposureElements.delete(entry.target);
          return;
        }

        intersectingExposureElements.add(entry.target);
        trackExposureElement(entry.target);
      });
    }, { threshold: EXPOSURE_THRESHOLD });
  }

  function parseScrollItemPayload(value) {
    if (!value || value.charAt(0) !== '{') return null;

    try {
      var payload = JSON.parse(value);
      if (!payload?.ecommerce || !Array.isArray(payload.ecommerce.items)) return null;
      return payload;
    } catch (error) {
      return null;
    }
  }

  function flushScrollExposureItems() {
    scrollExposureFlushScheduled = false;
    if (scrollExposureFlushTimer) {
      window.clearTimeout(scrollExposureFlushTimer);
      scrollExposureFlushTimer = null;
    }

    pendingScrollExposureItems.forEach(function (items, container) {
      if (!container.isConnected) return;

      container.querySelectorAll(SCROLL_ITEM_SELECTOR).forEach(function (item) {
        if (trackedScrollExposureItems.has(item)
          || !item.dataset.scrollItemName
          || !isElementVisibleInContainer(item, container)) {
          return;
        }

        trackedScrollExposureItems.add(item);
        items.add(item);
      });

      var itemValues = Array.from(container.querySelectorAll(SCROLL_ITEM_SELECTOR))
        .filter(function (item) { return items.has(item); })
        .map(function (item) { return item.dataset.scrollItemName; })
        .filter(Boolean);
      if (!itemValues.length) return;

      var params = getTrackingParams(container);
      if (!params) return;

      var itemPayloads = itemValues.map(parseScrollItemPayload);
      var isEcommerceBatch = itemPayloads.every(Boolean);
      if (isEcommerceBatch) {
        params.ecommerce = {
          currency: itemPayloads[0].ecommerce.currency,
          items: itemPayloads.reduce(function (allItems, payload) {
            return allItems.concat(payload.ecommerce.items);
          }, [])
        };
        delete params.third_category;
      } else {
        params.third_category = itemValues.join('-');
      }
      sendTrackingParams(params);
    });

    pendingScrollExposureItems.clear();
  }

  function scheduleScrollExposureFlush() {
    scrollExposureFlushScheduled = true;

    if (scrollExposureFlushTimer) {
      window.clearTimeout(scrollExposureFlushTimer);
    }

    // Swipers can reveal items from one visible group in adjacent observer frames.
    // Wait briefly for the group to settle, then report all newly visible items in
    // one payload. A later user scroll starts a new batch because tracked items are
    // never queued again.
    scrollExposureFlushTimer = window.setTimeout(
      flushScrollExposureItems,
      SCROLL_EXPOSURE_FLUSH_DELAY
    );
  }

  function queueScrollExposureItem(item) {
    if (trackedScrollExposureItems.has(item)) return;

    var container = item.closest(SCROLL_EXPOSURE_SELECTOR);
    if (!container || !item.dataset.scrollItemName) return;

    if (!isElementRendered(item)) {
      // A collapsed or animating list has no box yet. Retry on the next exposure
      // check instead of losing the report.
      deferredScrollExposureItems.add(item);
      return;
    }

    trackedScrollExposureItems.add(item);
    if (!pendingScrollExposureItems.has(container)) {
      pendingScrollExposureItems.set(container, new Set());
    }
    pendingScrollExposureItems.get(container).add(item);
    scheduleScrollExposureFlush();
  }

  function retryDeferredScrollExposureItems() {
    // Retrying a still-hidden item can add it back to this Set. Iterate a
    // snapshot so each item is attempted only once in this exposure check.
    Array.from(deferredScrollExposureItems).forEach(function (item) {
      if (!item.isConnected || trackedScrollExposureItems.has(item)) {
        deferredScrollExposureItems.delete(item);
        return;
      }

      if (isElementInViewport(item)) {
        deferredScrollExposureItems.delete(item);
        queueScrollExposureItem(item);
      }
    });
  }

  function createScrollExposureObserver() {
    if (!('IntersectionObserver' in window)) return null;

    return new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || entry.intersectionRatio < EXPOSURE_THRESHOLD) return;
        queueScrollExposureItem(entry.target);
      });
    }, { threshold: EXPOSURE_THRESHOLD });
  }

  function observeScrollExposureItem(item) {
    if (!item || observedScrollExposureItems.has(item)) return;
    observedScrollExposureItems.add(item);
    scrollExposureItems.add(item);

    if (scrollExposureObserver) {
      scrollExposureObserver.observe(item);
      return;
    }

    scheduleExposureCheck();
  }

  function observeScrollExposureElements(root) {
    if (!root) return;

    if (root.matches?.(SCROLL_ITEM_SELECTOR)) observeScrollExposureItem(root);
    root.querySelectorAll?.(SCROLL_ITEM_SELECTOR).forEach(observeScrollExposureItem);
  }

  function checkScrollExposureItems() {
    scrollExposureItems.forEach(function (item) {
      if (!item.isConnected) {
        scrollExposureItems.delete(item);
        return;
      }
      if (isElementInViewport(item)) queueScrollExposureItem(item);
    });
  }

  function checkIntersectingExposureElements() {
    exposureCheckScheduled = false;

    intersectingExposureElements.forEach(function (element) {
      if (!element.isConnected) {
        intersectingExposureElements.delete(element);
        return;
      }
      trackExposureElement(element);
    });

    if (!scrollExposureObserver) {
      checkScrollExposureItems();
    } else if (deferredScrollExposureItems.size) {
      retryDeferredScrollExposureItems();
    }
  }

  function scheduleExposureCheck() {
    if (exposureCheckScheduled) return;
    exposureCheckScheduled = true;

    if ('requestAnimationFrame' in window) {
      window.requestAnimationFrame(checkIntersectingExposureElements);
    } else {
      window.setTimeout(checkIntersectingExposureElements, 0);
    }
  }

  function observeExposureElement(element) {
    if (!element || observedExposureElements.has(element)) return;
    observedExposureElements.add(element);

    if (!exposureObserver) {
      intersectingExposureElements.add(element);
      scheduleExposureCheck();
      return;
    }

    exposureObserver.observe(element);
  }

  function observeExposureElements(root) {
    if (!root) return;

    if (root.matches?.(EXPOSURE_SELECTOR)) observeExposureElement(root);
    root.querySelectorAll?.(EXPOSURE_SELECTOR).forEach(observeExposureElement);
  }

  function onDocumentClick(event) {
    var target = event.target instanceof Element
      ? event.target.closest(CLICK_SELECTOR)
      : null;
    if (!target) return;

    trackClickElement(target);
  }

  function watchDynamicModules() {
    if (!('MutationObserver' in window) || !document.body) return;

    mutationObserver = new MutationObserver(function (records) {
      records.forEach(function (record) {
        if (record.type === 'attributes') {
          scheduleExposureCheck();
          return;
        }

        record.addedNodes.forEach(function (node) {
          if (node instanceof Element) {
            observeExposureElements(node);
            observeScrollExposureElements(node);
          }
        });
      });
    });

    mutationObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'open'],
      childList: true,
      subtree: true
    });
  }

  function init() {
    exposureObserver = createExposureObserver();
    scrollExposureObserver = createScrollExposureObserver();
    observeExposureElements(document);
    observeScrollExposureElements(document);
    watchDynamicModules();
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('mouseover', scheduleExposureCheck, { passive: true });
    document.addEventListener('focusin', scheduleExposureCheck);
    document.addEventListener('transitionend', scheduleExposureCheck);
    document.addEventListener('animationend', scheduleExposureCheck);

    if (!exposureObserver) {
      window.addEventListener('scroll', scheduleExposureCheck, { passive: true });
      window.addEventListener('resize', scheduleExposureCheck, { passive: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.brandCustomEvent = window.brandCustomEvent || {};
  window.brandCustomEvent.trackClickElement = trackClickElement;
  window.brandCustomEvent.trackExposureElement = trackExposureElement;
  window.brandCustomEvent.observeExposureElements = observeExposureElements;
})(window, document);
